import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { X, UserPlus, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  buildContactCreatePayload,
  getEmptyContactFormDefaults,
  resolvePhoneCodeOptionValue,
  validateContactCreateInput,
  EMAIL_REQUIRED_MESSAGE,
  FORENAME_REQUIRED_MESSAGE,
  sanitizeNationalPhoneInput,
} from "@/modules/contacts/contactFormUtils";
import {
  CONTACT_UNIQUENESS_DEBOUNCE_MS,
  hasContactInlineValidationErrors,
  isValidEmailForUniquenessCheck,
} from "@/modules/contacts/contactUniqueness";
import { useContactFieldUniqueness } from "@/modules/contacts/useContactFieldUniqueness";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import { branchesService } from "@/services/branchesService";
import { contactsService } from "@/services/contactsService";
import { useSettingsStore } from "@/store/settingsStore";
import { useCountries } from "@/hooks/useCountries";
import type { ContactSummary } from "@/types/entities";

interface ReachOutContactCreateModalProps {
  legalEntityId: string;
  /** Branch selected on the ReachOut page — pre-selected as default in the modal. */
  reachOutBranchId?: string;
  onClose: () => void;
  /** `createdBranchId` is the branch the contact was actually created under. */
  onCreated: (contact: ContactSummary, createdBranchId: string) => void;
}

export const ReachOutContactCreateModal: React.FC<ReachOutContactCreateModalProps> = ({
  legalEntityId,
  reachOutBranchId,
  onClose,
  onCreated,
}) => {
  // The reach-out branch is used only as the default selection; the user can
  // still pick a different branch and create the contact there.
  const defaultBranchId = reachOutBranchId?.trim() ?? "";
  const { branchId: settingsBranchId } = useSettingsStore();
  const { phoneCodeOptions, countryCodeOptions, loading: loadingCountries } = useCountries();

  const [forename, setForename] = useState("");
  const [surname, setSurname] = useState("");
  // Dial code (e.g. "+44") for the phone prefix; countryCode is the ISO alpha-2 (e.g. "GB").
  const [dialCode, setDialCode] = useState("+44");
  const [countryCode, setCountryCode] = useState("GB");
  const [mobilePhone, setMobilePhone] = useState("");
  const [email, setEmail] = useState("");
  const [branchId, setBranchId] = useState("");
  const [externalBranchId, setExternalBranchId] = useState("");
  const [branchSourceCrm, setBranchSourceCrm] = useState("");
  const [branchOptions, setBranchOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const branchesByIdRef = useRef(new Map<string, { externalId: string; sourceCrm: string }>());

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const { onOverlayClick } = useModalDismiss(true, onClose);

  const applyBranchSelection = useCallback((selectedBranchId: string) => {
    setBranchId(selectedBranchId);
    const meta = branchesByIdRef.current.get(selectedBranchId);
    setExternalBranchId(meta?.externalId ?? "");
    setBranchSourceCrm(meta?.sourceCrm ?? "");
  }, []);

  useEffect(() => {
    if (!legalEntityId) {
      setBranchId("");
      setBranchOptions([]);
      setExternalBranchId("");
      setBranchSourceCrm("");
      branchesByIdRef.current.clear();
      return;
    }

    let cancelled = false;
    setLoadingBranches(true);
    branchesService
      .list({ legalEntityId }, { page: 0, size: 200 })
      .then((res) => {
        if (cancelled) return;
        const pool = new Map<string, { externalId: string; sourceCrm: string }>();
        const options = res.content.map((b) => {
          pool.set(b.id, {
            externalId: b.externalId ?? "",
            sourceCrm: b.sourceCrm?.trim() ?? "",
          });
          return {
            value: b.id,
            label: b.name?.trim() || b.externalId?.trim() || b.id,
          };
        });
        branchesByIdRef.current = pool;
        setBranchOptions(options);

        const preferred =
          defaultBranchId && pool.has(defaultBranchId)
            ? defaultBranchId
            : settingsBranchId && pool.has(settingsBranchId)
              ? settingsBranchId
              : options[0]?.value ?? "";
        if (preferred) {
          applyBranchSelection(preferred);
        } else {
          setBranchId("");
          setExternalBranchId("");
          setBranchSourceCrm("");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setBranchOptions([]);
        setBranchId("");
        setExternalBranchId("");
        setBranchSourceCrm("");
        branchesByIdRef.current.clear();
      })
      .finally(() => {
        if (!cancelled) setLoadingBranches(false);
      });

    return () => {
      cancelled = true;
    };
  }, [legalEntityId, defaultBranchId, settingsBranchId, applyBranchSelection]);

  // Resolve which composite "+44|GB" option the dial-code dropdown should show, based on
  // 1) the dial code itself, 2) the selected/received countryCode, 3) MAJOR_COUNTRY_FOR_DIAL_CODE.
  const phoneCodeValue = useMemo(
    () => resolvePhoneCodeOptionValue(dialCode, countryCode, phoneCodeOptions),
    [dialCode, countryCode, phoneCodeOptions],
  );

  const fullPhoneNumber = useMemo(() => {
    const national = mobilePhone.trim().replace(/^0+/, "");
    return national ? `${dialCode}${national}` : "";
  }, [dialCode, mobilePhone]);

  const uniquenessScope = useMemo(
    () => contactPropertyListScope(legalEntityId, branchId),
    [legalEntityId, branchId],
  );

  const nationalPhone = mobilePhone.trim();

  const uniquenessErrors = useContactFieldUniqueness({
    open: true,
    firstEmail: email,
    firstPhone: fullPhoneNumber,
    secondEmail: "",
    secondPhone: "",
    secondPersonEnabled: false,
    scope: uniquenessScope,
  });

  const trimmedEmail = email.trim();
  const debouncedEmail = useDebouncedValue(trimmedEmail, CONTACT_UNIQUENESS_DEBOUNCE_MS);
  const emailFormatError =
    !trimmedEmail || debouncedEmail !== trimmedEmail
      ? ""
      : !isValidEmailForUniquenessCheck(debouncedEmail)
        ? "Enter a valid email address"
        : "";

  const fieldErrors = {
    forename: forename.trim() ? null : "First name is required",
    surname: surname.trim() ? null : "Surname is required",
    phone: !nationalPhone
      ? "Mobile phone is required"
      : uniquenessErrors.mobilePhone || null,
    email: !trimmedEmail
      ? "Email address is required"
      : !isValidEmailForUniquenessCheck(trimmedEmail)
        ? "Enter a valid email address"
        : uniquenessErrors.email || null,
    branch:
      !branchId || !externalBranchId.trim() || !branchSourceCrm.trim()
        ? "Branch is required"
        : null,
  };
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setShowValidation(true);
    if (hasFieldErrors || submitting || loadingBranches) return;
    if (hasContactInlineValidationErrors(uniquenessErrors)) return;

    const form = getEmptyContactFormDefaults();
    form.person[0].forename = forename.trim();
    form.person[0].surname = surname.trim();
    form.person[0].emailAddresses[0].emailAddress = email.trim();
    form.person[0].phoneNumbers[0].number = fullPhoneNumber;

    if (!forename.trim()) {
      setError(FORENAME_REQUIRED_MESSAGE);
      return;
    }
    if (!email.trim()) {
      setError(EMAIL_REQUIRED_MESSAGE);
      return;
    }
    if (!isValidEmailForUniquenessCheck(email.trim())) {
      setError("Enter a valid email address");
      return;
    }

    form.countryCode = countryCode.trim();

    const validation = validateContactCreateInput({
      legalEntityId,
      branchId,
      externalBranchId,
      sourceCrm: branchSourceCrm,
      countryCode: form.countryCode?.trim() ?? "",
      category: form.category ?? "",
      forename: forename.trim(),
      surname: surname.trim(),
      email: email.trim(),
      phone: nationalPhone,
      postcode: form.postcode?.trim() ?? "",
      secondPersonEnabled: false,
    });

    if (!validation.success) {
      setError(validation.message);
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildContactCreatePayload(form, {
        legalEntityId,
        branchId,
        externalBranchId,
        sourceCrm: branchSourceCrm,
        secondPersonEnabled: false,
      });

      const newContact = await contactsService.create(payload);
      if (defaultBranchId && branchId !== defaultBranchId) {
        const branchLabel =
          branchOptions.find((o) => o.value === branchId)?.label ?? "a different branch";
        toast.info(
          `Contact created in ${branchLabel}. It won't appear in this reach-out because a different branch is selected.`,
        );
      }
      onCreated(newContact, branchId);
    } catch (err: unknown) {
      const data =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data
          : err && typeof err === "object" && "data" in err
            ? (err as { data?: { message?: string } }).data
            : err;
      const message =
        data && typeof data === "object" && "message" in data && typeof data.message === "string"
          ? data.message
          : err instanceof Error
            ? err.message
            : "Failed to create contact.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      onClick={onOverlayClick}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-bridge-accent/10 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-bridge-accent" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Quick Create Contact</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-200 text-slate-500 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2.5 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="font-medium">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-bridge-muted ml-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={forename}
                onChange={(e) => setForename(e.target.value)}
                placeholder="e.g. John"
                required
              />
              {showValidation && <FieldError message={fieldErrors.forename} />}
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold uppercase tracking-wider text-bridge-muted ml-1">
                Surname <span className="text-red-500">*</span>
              </label>
              <Input
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                placeholder="e.g. Doe"
                required
              />
              {showValidation && <FieldError message={fieldErrors.surname} />}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold uppercase tracking-wider text-bridge-muted ml-1">
              Mobile Phone <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="w-1/3">
                <MenuSelect
                  options={phoneCodeOptions}
                  value={phoneCodeValue}
                  onChange={(v) => {
                    const [nextDialCode, nextCountry] = v.split("|");
                    setDialCode(nextDialCode);
                    if (nextCountry) setCountryCode(nextCountry);
                  }}
                  placeholder="Code"
                  loading={loadingCountries}
                  searchable
                />
              </div>
              <div className="flex-1">
                <Input
                  value={mobilePhone}
                  onChange={(e) => setMobilePhone(sanitizeNationalPhoneInput(e.target.value))}
                  placeholder="7123456789"
                  inputMode="numeric"
                  required
                />
              </div>
            </div>
            {uniquenessErrors.mobilePhone ? (
              <p className="text-xs text-red-500 font-medium mt-1 ml-1">{uniquenessErrors.mobilePhone}</p>
            ) : showValidation ? (
              <FieldError message={fieldErrors.phone} />
            ) : null}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold uppercase tracking-wider text-bridge-muted ml-1">
              Country Code
            </label>
            <MenuSelect
              options={countryCodeOptions}
              value={countryCode}
              onChange={setCountryCode}
              placeholder="Select country"
              loading={loadingCountries}
              searchable
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-bold uppercase tracking-wider text-bridge-muted ml-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john.doe@example.com"
              required
              pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
              title="Enter a valid email address (e.g. name@company.com)"
            />
            {emailFormatError ? (
              <p className="text-xs text-red-500 font-medium mt-1 ml-1">{emailFormatError}</p>
            ) : uniquenessErrors.email ? (
              <p className="text-xs text-red-500 font-medium mt-1 ml-1">{uniquenessErrors.email}</p>
            ) : showValidation ? (
              <FieldError message={fieldErrors.email} />
            ) : null}
          </div>

          <div className="space-y-1">
            <MenuSelect
              label="Branch"
              options={branchOptions}
              value={branchId}
              onChange={applyBranchSelection}
              placeholder={
                loadingBranches
                  ? "Loading branches..."
                  : branchOptions.length > 0
                    ? "Select branch"
                    : "No branches available"
              }
              disabled={loadingBranches || branchOptions.length === 0}
            />
            {showValidation && <FieldError message={fieldErrors.branch} />}
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={submitting}
              className="px-6 rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="px-6 rounded-xl bg-[#009BF5] hover:bg-[#0085d4] text-white shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Contact"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

function FieldError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="mt-1 flex animate-in items-center gap-1.5 text-xs font-medium text-red-500 fade-in slide-in-from-top-1 duration-200">
      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}
